import {
  AuthConfiguration,
  AuthContext,
  useAuth,
  useAutoCookieFlow,
} from "@noom/hive-auth";
import React, { ReactNode, useCallback } from "react";
import { appConfig } from "./config";
import { NoomLoader } from "./components/noom-loader/NoomLoader";
import { useOnce } from "./hooks/lifecycle";
import { getInAppAutoCookie } from "./utils/authCookieParser";
import {
  clearLoginInternalLocation,
  getLoginInternalLocation,
} from "./utils/auth";

const authConfig: AuthConfiguration = {
  storage: "sessionStorage",
  identityServer: {
    serverUrl: appConfig.IDENTITY_SERVER_URL,
    clientId: "buyflow-client",
    scope: "COACH_USER",
  },
  autoCookie: {
    autoCookieName: getInAppAutoCookie() ? "autologin_data" : "identity_data",
  },
};

function AutoCookieGuard({ children }: { children: ReactNode }) {
  const { isAvailable, inProgress, login, error } = useAutoCookieFlow();
  const { isAuthenticated } = useAuth();

  useOnce(() => {
    if (!isAuthenticated && isAvailable && !inProgress && !error) {
      login(window.location.href);
    }
  });

  const shouldShowLoader =
    inProgress || (!isAuthenticated && isAvailable && !error);
  if (shouldShowLoader) {
    return <NoomLoader fullScreen />;
  }
  return <>{children}</>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const handleLoginSuccess = useCallback(() => {
    // Defer so that navigate(...) inside loginCallback has already run
    if (getLoginInternalLocation()) {
      setTimeout(() => {
        clearLoginInternalLocation();
        // Reload the page to ensure the app is re-initialized using
        // the proper redirect URL, serverContext, etc.
        window.location.reload();
      }, 0);
    }
  }, []);

  return (
    <AuthContext config={authConfig} onLoginSuccess={handleLoginSuccess}>
      <AutoCookieGuard>{children}</AutoCookieGuard>
    </AuthContext>
  );
}
